import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Search, ShieldCheck, XCircle, Building2, Calendar, FileText, Briefcase } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { PublicLayout } from "../components/PublicLayout";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InfoRow = ({ icon: Icon, label, value, testId }) => (
  <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
    <Icon className="w-5 h-5 text-slate-500 mt-0.5" />
    <div>
      <p className="text-sm text-slate-500">{label}</p>
      <p className="font-semibold text-slate-800" data-testid={testId}>{value || "—"}</p>
    </div>
  </div>
);

const AuthenticResult = ({ data }) => (
  <Card className="shadow-lg border-0 overflow-hidden" data-testid="quittance-authentic">
    <div className="p-5 bg-emerald-50 border-b border-emerald-200 flex items-center gap-3">
      <ShieldCheck className="w-9 h-9 text-emerald-600" />
      <div>
        <p className="text-lg font-bold text-emerald-700">Quittance authentique</p>
        <p className="text-sm text-emerald-700/80">Ce document a bien été émis par l'Inspection Générale du Travail</p>
      </div>
    </div>
    <CardContent className="pt-6 space-y-4">
      <div className="p-4 bg-[#1B4D3E]/5 rounded-lg border border-[#1B4D3E]/20">
        <p className="text-sm text-slate-500">N° de quittance</p>
        <p className="text-2xl font-mono font-bold text-[#1B4D3E]" data-testid="quittance-numero">{data.numero}</p>
      </div>
      <InfoRow icon={Building2} label="Raison sociale" value={data.raison_sociale} testId="quittance-raison-sociale" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InfoRow icon={FileText} label="Exercice" value={data.exercice} testId="quittance-exercice" />
        <InfoRow icon={Calendar} label="Date d'émission" value={data.date_emission} testId="quittance-date" />
      </div>
      <InfoRow
        icon={Briefcase}
        label="Cabinet déclarant"
        value={data.cabinet_nom && `${data.cabinet_nom} — N° habilitation ${data.cabinet_habilitation || "—"}`}
        testId="quittance-cabinet"
      />
    </CardContent>
  </Card>
);

const NotFoundResult = ({ numero }) => (
  <Card className="shadow-lg border-0 border-l-4 border-l-red-500" data-testid="quittance-not-found">
    <CardContent className="pt-6 flex items-center gap-4">
      <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center shrink-0">
        <XCircle className="w-6 h-6 text-red-600" />
      </div>
      <div>
        <p className="font-semibold text-slate-800">Quittance introuvable</p>
        <p className="text-sm text-slate-600">
          Le numéro « <span className="font-mono font-bold">{numero}</span> » ne correspond à aucune quittance émise par l'IGT.
          Ce document pourrait être falsifié : contactez l'IGT (info@igt.gov.gn).
        </p>
      </div>
    </CardContent>
  </Card>
);

export default function QuittanceVerificationPage() {
  const { numero } = useParams();
  const navigate = useNavigate();
  const [input, setInput] = useState(numero || "");
  const [state, setState] = useState({ loading: false, data: null, notFound: false });

  useEffect(() => {
    if (!numero) return setState({ loading: false, data: null, notFound: false });
    setInput(numero.toUpperCase());
    setState({ loading: true, data: null, notFound: false });
    axios
      .get(`${API_URL}/public/verifier-quittance/${encodeURIComponent(numero)}`)
      .then((r) => setState({ loading: false, data: r.data, notFound: false }))
      .catch((e) => {
        if (e.response?.status === 404) return setState({ loading: false, data: null, notFound: true });
        setState({ loading: false, data: null, notFound: false });
        toast.error(e.response?.status === 429 ? "Trop de vérifications, réessayez dans une minute" : "Erreur lors de la vérification");
      });
  }, [numero]);

  const submit = (e) => {
    e.preventDefault();
    if (!input.trim()) return toast.error("Veuillez saisir un numéro de quittance");
    navigate(`/verifier/${input.trim().toUpperCase()}`);
  };

  return (
    <PublicLayout>
      <div className="text-center mb-8">
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-2">Vérification de Quittance</h2>
        <p className="text-slate-600">Vérifiez l'authenticité d'une quittance de déclaration du bilan social</p>
      </div>
      <Card className="mb-8 shadow-lg border-0">
        <CardHeader className="bg-slate-50 border-b">
          <CardTitle className="text-lg flex items-center gap-2">
            <Search className="w-5 h-5 text-[#1B4D3E]" />
            Numéro de quittance
          </CardTitle>
          <CardDescription>Scannez le QR code de la quittance ou saisissez son numéro (ex : Q0000001)</CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value.toUpperCase())}
              placeholder="Q0000001"
              className="text-lg !h-12 font-mono flex-1"
              data-testid="quittance-search-input"
            />
            <Button type="submit" disabled={state.loading} className="h-12 px-8 bg-[#1B4D3E] hover:bg-[#2d7a62]" data-testid="quittance-search-btn">
              {state.loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <><Search className="w-5 h-5 mr-2" />Vérifier</>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
      {state.data && <AuthenticResult data={state.data} />}
      {state.notFound && <NotFoundResult numero={numero.toUpperCase()} />}
    </PublicLayout>
  );
}
